import { mapValues, range } from "lodash";
import type {
  AddParticipantListRequest,
  ParticipantListCombinationPerson,
  ParticipantListResponse,
  ParticipantListTemplateResponse,
} from "./participantListApi";
import type { ParticipantsForCombination, Person } from "./getRoomsCells";

export type DeepRequired<T> = {
  [K in keyof T]: Required<DeepRequired<T[K]>>;
};

export function defaultPerson(): Person {
  return {
    type: "PARTICIPANT" as const,
    name: "",
    surname: "",
    dateOfBirth: undefined,
    gender: undefined,
    citizenship: "",
    documentNumber: "",
    documentExpiryDate: "",
  };
}

export function stateForTemplate(
  template: ParticipantListTemplateResponse,
  previousParticipants: Omit<ParticipantListResponse, "id" | "createdAt"> | undefined,
): DeepRequired<ParticipantsForCombination>[] {
  if (!previousParticipants || template.combinations.length !== previousParticipants.combinations.length) {
    return template.combinations.map((t) =>
      t.rooms.reduce<Record<string, { rooms: { people: ReturnType<typeof defaultPerson>[] }[] }>>((acc, r) => {
        const key = `${r.numberOfPeople}`;
        const newRooms = range(0, r.count).map(() => ({
          people: range(0, r.numberOfPeople).map(() => defaultPerson()),
        }));
        acc[key] = {
          rooms: [...(acc[key]?.rooms ?? []), ...newRooms],
        };
        return acc;
      }, {}),
    );
  }
  return template.combinations.map((combination, combinationIdx) => {
    const previousCombination = previousParticipants.combinations[combinationIdx] ?? {};
    return combination.rooms.reduce<{
      state: Record<string, { rooms: { people: (ReturnType<typeof defaultPerson> | null)[] }[] }>;
      offsets: Record<string, number>;
    }>(
      (acc, r) => {
        const key = `${r.numberOfPeople}`;
        const offset = acc.offsets[key] ?? 0;
        const previousRooms = previousCombination[key]?.rooms ?? [];
        const newRooms = range(0, r.count).map((roomIdx) => {
          const previousPeople = previousRooms[offset + roomIdx]?.people ?? [];
          return {
            people: range(0, r.numberOfPeople).map((pIdx) => {
              const previousPerson = previousPeople[pIdx];
              if (previousPerson === null) {
                return null;
              }
              return {
                type: previousPerson?.type ?? "PARTICIPANT",
                name: previousPerson?.name ?? "",
                surname: previousPerson?.surname ?? "",
                dateOfBirth: previousPerson?.dateOfBirth ?? undefined,
                gender: previousPerson?.gender ?? undefined,
                citizenship: previousPerson?.citizenship ?? "",
                documentNumber: previousPerson?.documentNumber ?? "",
                documentExpiryDate: previousPerson?.documentExpiryDate ?? "",
              };
            }),
          };
        });
        acc.state[key] = {
          rooms: [...(acc.state[key]?.rooms ?? []), ...newRooms],
        };
        acc.offsets[key] = offset + r.count;
        return acc;
      },
      { state: {}, offsets: {} },
    ).state;
  });
}

export function mergePersonChanges(
  existing: DeepRequired<Person> | null,
  update: Partial<Person> | null,
): DeepRequired<Person> | null {
  if (update === null) {
    return null;
  }
  if (existing === null) {
    return { ...defaultPerson(), ...update } as DeepRequired<Person>;
  }
  return { ...existing, ...update };
}

export function toRequestPayload(
  combinationsState: DeepRequired<ParticipantsForCombination>[],
  template: ParticipantListTemplateResponse,
  plateNumber: string,
  driverPhone: string,
  tourLeaderPhone: string,
): AddParticipantListRequest {
  return {
    combinations: combinationsState.map((c) => mapValues(c, (r) => r.rooms)),
    bus: template.isBusRequired ? { plateNumber, driverPhone } : undefined,
    tourLeader: { tourLeaderPhone: tourLeaderPhone || undefined },
  };
}

export function lastSavedPayload(
  previous: Omit<ParticipantListResponse, "id" | "createdAt"> | undefined,
  template: ParticipantListTemplateResponse,
): AddParticipantListRequest {
  if (previous) {
    return {
      combinations: previous.combinations.map((c) =>
        Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v.rooms])),
      ),
      bus: previous.bus,
      tourLeader: previous.tourLeader,
    };
  }
  const emptyState = stateForTemplate(template, undefined);
  return toRequestPayload(emptyState, template, "", "", "");
}

export function normalizePersonForComparison(
  p: ParticipantListCombinationPerson | null,
): ParticipantListCombinationPerson | null {
  if (!p) {
    return null;
  }
  return {
    type: p.type,
    name: p.name ?? "",
    surname: p.surname ?? "",
    dateOfBirth: p.dateOfBirth,
    gender: p.gender,
    citizenship: p.citizenship ?? "",
    documentNumber: p.documentNumber ?? "",
    documentExpiryDate: p.documentExpiryDate ?? "",
  };
}

export function normalizePayloadForComparison(payload: AddParticipantListRequest): AddParticipantListRequest {
  return {
    ...payload,
    tourLeader: {
      tourLeaderPhone: payload.tourLeader?.tourLeaderPhone || undefined,
    },
    bus:
      payload.bus !== undefined
        ? {
            plateNumber: payload.bus.plateNumber ?? "",
            driverPhone: payload.bus.driverPhone ?? "",
          }
        : undefined,
    combinations: payload.combinations.map((combo) => {
      const normalized: Record<string, { people: (ParticipantListCombinationPerson | null)[] }[]> = {};
      for (const [key, rooms] of Object.entries(combo)) {
        normalized[key] = rooms.map((room) => ({
          people: room.people.map((p) => normalizePersonForComparison(p)),
        }));
      }
      return normalized;
    }),
  };
}
